/**
 * A terminal form state is announced twice: once by the transition into it, and
 * again a render loop later by the plugin sitting on it calling completeEvent.
 * Both routes legitimately submit — a Final node with required fields is only
 * reached by the first, and a plugin-supplied state path only by the second —
 * so the second is joined to the first rather than removed.
 */
export interface FinalSubmitLatch {
  statePath: string;
  promise: Promise<void>;
}

/**
 * True when this submit is the second announcement of a state path already
 * completing, and should await that submit instead of issuing another.
 *
 * Non-final submits are never joined: they carry changed form values and each
 * one is a distinct save. A retry after a failure is never joined either, or a
 * participant whose completing submit failed could not get out of the form.
 */
export function shouldJoinFinalSubmit(
  latch: FinalSubmitLatch | undefined,
  statePath: string,
  isFinal: boolean,
  isResubmit: boolean
): boolean {
  return Boolean(
    isFinal && !isResubmit && latch && latch.statePath === statePath
  );
}

export interface FinalSubmitTracker {
  /** The in-flight completing submit to await, or undefined to issue a new one. */
  join(
    statePath: string,
    isFinal: boolean,
    isResubmit: boolean
  ): Promise<void> | undefined;
  /** Records a completing submit so the second announcement joins it. */
  track(statePath: string, promise: Promise<void>): void;
  /**
   * Drops the latch if it belongs to this state path, so the next submit for it
   * is issued afresh. Scoped by path because the commonest failure — an HTTP
   * error — resolves rather than rejects, so it cannot release itself, and an
   * unscoped release would let an unrelated failure clear a live latch.
   */
  releaseIf(statePath: string): void;
}

/**
 * Holds the completing submit for a state path.
 *
 * A rejected submit releases the latch on its own. Not every failure surfaces
 * through the upsert hook's error state — a missing intended start date throws
 * before the request, and the session refresh throws after it — so relying on
 * that state alone would leave a rejected promise latched, and the participant's
 * retry would join the failure instead of re-submitting.
 */
export function createFinalSubmitTracker(): FinalSubmitTracker {
  let latch: FinalSubmitLatch | undefined;

  return {
    join(statePath, isFinal, isResubmit) {
      return shouldJoinFinalSubmit(latch, statePath, isFinal, isResubmit)
        ? latch?.promise
        : undefined;
    },
    track(statePath, promise) {
      latch = { statePath, promise };
      // Compared by identity so a newer submit's latch is never cleared by an
      // older one rejecting.
      promise.catch(() => {
        if (latch?.promise === promise) {
          latch = undefined;
        }
      });
    },
    releaseIf(statePath) {
      if (latch?.statePath === statePath) {
        latch = undefined;
      }
    }
  };
}

/**
 * Issues a submit, or awaits the in-flight completing one for this path.
 *
 * The retry flag is consumed only when this call starts a new submit. Leaving
 * it set would let a second terminal announcement bypass the latch too, and
 * both upserts could create separate event records before an entity id exists.
 */
export function startOrJoinFinalSubmit(
  tracker: FinalSubmitTracker,
  statePath: string,
  isFinal: boolean,
  retry: { current: boolean },
  start: () => Promise<void>
): Promise<void> {
  const joined = tracker.join(statePath, isFinal, retry.current);
  if (joined) {
    return joined;
  }
  retry.current = false;
  const submission = start();
  if (isFinal) {
    tracker.track(statePath, submission);
  }
  return submission;
}
